import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "../stores/auth.store";
import { useAuthPermissions } from "../hooks/useAuthPermissions";
import PermissionDeniedGate from "./PermissionDeniedGate";
import RouteLoadingFallback from "./RouteLoadingFallback";
import { loginPathFor } from "../utils/loginRedirect";

interface AuthRouteProps {
  children: React.ReactNode;
  /** Full permission string (when not org-scoped). */
  requiredPermission?: string;
  /** Build permission from the active organization id. */
  orgScopedPermission?: (orgId: string) => string;
  legacyRoles?: readonly string[];
}

/**
 * Authenticated route with optional permission gate (dual-mode legacy fallback).
 */
const AuthRoute: React.FC<AuthRouteProps> = ({
  children,
  requiredPermission,
  orgScopedPermission,
  legacyRoles,
}) => {
  const { isAuthenticated, meLoaded } = useAuthStore();
  const { can, orgId, isPlatformAdmin } = useAuthPermissions();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to={loginPathFor(`${location.pathname}${location.search}`)} replace />;
  }

  if (!isPlatformAdmin && (orgScopedPermission || requiredPermission)) {
    const hasOrgAccess =
      Boolean(orgScopedPermission && orgId) &&
      can(orgScopedPermission!(orgId!), legacyRoles);
    const hasPlatformAccess = Boolean(
      requiredPermission && can(requiredPermission, legacyRoles),
    );

    if (!hasOrgAccess && !hasPlatformAccess) {
      // The token can lag the server; deny only once /user/me has answered.
      return meLoaded ? <PermissionDeniedGate /> : <RouteLoadingFallback />;
    }
  }

  return <>{children}</>;
};

export default AuthRoute;
